import { trackFlowEnteredAnalytics } from '@util/FlowUtils';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Gender } from '../../businessLogic/AssessmentQuestionTypes';
import { FlowConfig, ScreenType, SharedScreenName } from '../../config/FlowTypes';
import { NervaControlScreenName } from '../../config/nerva/NervaControl';
import { ActionType } from '../../store/ActionTypes';
import { useAppDispatch, useAppStore } from '../../store/Store';
import '../../theme.less';
import { PreloadFlowImages } from '../assets/preloadFlowImages/PreloadFlowImages';
import { FlowStack } from '../flows/FlowStack';

const flowConfig: FlowConfig<
  NervaControlScreenName.DownloadAppAccessCode | SharedScreenName.Gender
> = {
  name: 'YouHaveAccess',
  showProgressBar: true,
  startEvent: undefined,
  screens: [
    {
      // This gender screen is included only to overcome a bug where a single screen flow is seen to be acyclic,
      // and throws an `Graph is not a acyclic` error. Having two screens in the flow overcomes that bug.
      // We also chose Gender over all other screens as it is the first screen in (most) flow configs, so if
      // the config looks for a 'Gender' screen while this flow is loading, and keeps looking for Gender afer this
      // has loaded, it will not crash.
      screenName: SharedScreenName.Gender,
      type: ScreenType.SingleSelect,
      layout: {
        body: [
          'Great, we will start by understanding a bit more about you and your IBS history.',
          'These responses will help us personalize your program.',
          'What’s your gender?',
        ],
        choices: [Gender.Male, Gender.Female, Gender.Nonbinary, Gender.Other, Gender.Unstated],
      },
      nextScreen: NervaControlScreenName.DownloadAppAccessCode,
    },
    {
      screenName: NervaControlScreenName.DownloadAppAccessCode,
      type: ScreenType.DownloadApp,
      layout: {
        title: ['You’re all set!'],
        alreadyPaid: true,
        body: [
          'Use the same email and password you signed up with to log in to the app and start your program.',

          'No payment is needed as you have been given free access to Nerva through your referral.',
        ],
      },
    },
  ],
};

export const YouHaveAccess: FunctionComponent = () => {
  const dispatch = useAppDispatch();

  const purchase = useAppStore((state) => state.purchase);

  const [isLoading, setIsLoading] = useState(true);
  useEffect(function trackAnalyticsEvent() {
    trackFlowEnteredAnalytics(purchase, flowConfig.name);
  }, []);

  useEffect(function initializeStorePage() {
    dispatch({
      type: ActionType.SET_PAGE,
      page: flowConfig.screens[1].screenName,
    });
    setIsLoading(false);
  }, []);

  return isLoading ? null : (
    <>
      <PreloadFlowImages flowConfig={flowConfig} />
      <FlowStack config={flowConfig} />
    </>
  );
};
